<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import Fa from 'svelte-fa'
    import { faArrowRight, faCheck, faRobot } from '@fortawesome/free-solid-svg-icons'
    import dayjs from 'dayjs'
    import _isToday from 'dayjs/plugin/isToday'
    import type { Category } from '../../../modules/financialApi/categories/category'
    import { EntryTypeEnum } from '../../../modules/financialApi/entries/entry.enum'
    import type { CreateEntryDto } from '../../../modules/financialApi/entries/dto/create-entry.dto'
    import type { Account } from '../../../modules/financialApi/accounts/account'
    import { OFXParserService } from '../../../modules/utils/ofx-parser.service'
    import type { Entry } from '../../../modules/financialApi/entries/entry'
    import AccountSelect from '../../financial/accounts/account-select.svelte'
    import PluggyConnector from '../pluggy-connector.svelte'
    import type { PluggyTransactionDto } from '../../../modules/financialApi/accounts/dto/pluggy-transactions.dto'
    import Financial_4AccountsImportItemV2 from './financial-4-accounts-import-item-v2.svelte'
    import type { Classification } from '../../../modules/financialApi/classifications/classification'
    import Popover from '../../_common/popover.svelte'
    import Financial_4AccountsClassification from './financial-4-accounts-classification.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    dayjs.extend(_isToday)

    const dispatch = createEventDispatcher()

    let categoriesCredit: Category[] = []
    let categoriesDebit: Category[] = []
    let classification: Classification[] = []

    let show = false
    let step = 1
    onMount(async () => {
        categoriesCredit = await window.api.financials.categories.getAll({ cd: EntryTypeEnum.credit })
        categoriesDebit = await window.api.financials.categories.getAll({ cd: EntryTypeEnum.debit })
        classification = await window.api.financials.classifications.getAll()
        console.log('step', step.toString())
        step = 1
    })

    async function updateAccountSource(accountId: string, source: 'ofx' | 'open') {
        return await window.api.financials.accounts.update(accountId, { source })
    }

    async function saveEntry(entry: CreateEntryDto) {
        await window.api.financials.entries.upsert(entry)
    }

    async function onEntryChanged(data) {
        if (!data?.detail?.categoryId) return
        let entry: CreateEntryDto = data.detail
        entry.classificationId = null
        await saveEntry(entry)
    }

    function clearText(text: string) {
        text = text
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
        return text
    }

    function getAccrualDate(entry: CreateEntryDto, found: Classification): { month: number; year: number } {
        if (!found.accrualYear && !found.accrualMonth && found.accrualYear !== 0 && found.accrualMonth !== 0) return { month: null, year: null }
        if (found.accrualType === 'absolute') {
            return { month: found.accrualMonth, year: found.accrualMonth }
        }
        if (found.accrualType === 'relative') {
            let date = new Date(entry.paymentAt)
            //
            if (found.accrualYear) {
                let year = date.getFullYear() + found.accrualYear
                return { month: null, year }
            }
            if (!found.accrualMonth !== undefined && found.accrualMonth !== null) {
                date.setMonth(date.getMonth() + found.accrualMonth)
                let month = date.getMonth() + 1 // +1 pois o mês começa em 0
                return { month, year: date.getFullYear() }
            }
        }
    }
    async function evaluateClassification(entries: CreateEntryDto[]) {
        for (let entry of entries) {
            if (!entry.categoryId) {
                const found = classification.find((el) => clearText(entry.description).includes(clearText(el.match)))
                if (found) {
                    entry.categoryId = found.categoryId
                    entry.classificationId = found.classificationId
                    const accrualDate = getAccrualDate(entry, found)
                    entry.accrualMonth = accrualDate.month
                    entry.accrualYear = accrualDate.year
                    await saveEntry(entry)
                    await new Promise((resolve) => setTimeout(resolve, 25))
                }
            } // criado para classificação retroativa das competências
            else if (window.configs.financialShowAccrual && !entry.accrualMonth && !entry.accrualYear) {
                const found = classification.find((el) => clearText(entry.description).includes(clearText(el.match)))
                if (found) {
                    const accrualDate = getAccrualDate(entry, found)
                    if (accrualDate.month || accrualDate.year) {
                        entry.accrualMonth = accrualDate.month
                        entry.accrualYear = accrualDate.year
                        await saveEntry(entry)
                        await new Promise((resolve) => setTimeout(resolve, 25))
                    }
                }
            }
        }
        return entries
    }

    let showClassification = false
    let classificationId = null
    let classificationSugestion: {
        accountId: string
        creditDebit: EntryTypeEnum
        description: string
        categoryId: string
    } = null
    function goClassificationForm(ev) {
        const entry: Entry = ev.detail
        classificationId = entry.classificationId
        classificationSugestion = {
            accountId: entry.accountId,
            categoryId: entry.categoryId,
            creditDebit: entry.type,
            description: entry.description,
        }
        showClassification = true
    }

    function goClassification() {
        classificationId = null
        showClassification = true
    }

    function closeClassification() {
        showClassification = false
        classificationId = null
    }

    function close() {
        for (const item of entries) {
            if (!item.categoryId) {
                if (!confirm('Nem todos os itens foram importados.\nDeseja mesmo sair?')) {
                    return
                }
                break
            }
        }
        dispatch('change')
    }
    let filterDays = 999

    // ==============================================================================================

    async function startImport() {
        if (account.source === 'ofx') {
            await loadOFX()
        } else {
            await loadOpenFinance()
        }
    }
    // OFX FILES ====================================================================================
    let file
    let fileContent: string = null
    function fileChanged() {
        fileContent = null
        if (!file.files || !file.files[0]) {
            return
        }
        let filePath = file.files[0]
        let reader = new FileReader()
        reader.readAsText(filePath, 'utf-8')
        let reload = false
        reader.onload = (e) => {
            let content: string = reader.result as string
            if (content != null) {
                if (reload === false) {
                    let knownChars = ['�', '�', '�', '�', '�', '�', '�', '�', '�', '�']
                    for (const chr of knownChars) {
                        if (content.includes(chr)) {
                            reload = true
                            reader.readAsText(filePath, 'iso-8859-1')
                            return
                        }
                    }
                }
                fileContent = content
            }
        }
    }

    let entries: CreateEntryDto[] = []
    let account: Account = null
    async function loadOFX() {
        if (!account?.accountId) {
            window.error('Selecione uma conta bancária')
            return
        }
        if (!fileContent) {
            window.error('Selecione um arquivo OFX')
            return
        }
        const ofx = new OFXParserService()
        let data = ofx.toJSON(fileContent)
        // num conta
        console.log('ofx', data)
        console.log('banco', data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKACCTFROM.BRANCHID)
        console.log('conta', data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKACCTFROM.ACCTID)
        console.log('entries', data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKTRANLIST)
        let accountNumber: string = data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKACCTFROM.ACCTID
        const persistedAccountHig = account.account?.replace('-', '') || ''
        const ofxAccountHig = accountNumber.replace('-', '')
        if (ofxAccountHig.substring(ofxAccountHig.length - 4) !== persistedAccountHig.substring(persistedAccountHig.length - 4)) {
            window.error([
                'O arquivo não pertence a esta conta.',
                'Caso o arquivo esteja correto verifique se o nome do banco e o número da conta estão corretos no cadastro da conta selecionada.',
            ])
            return
        }

        entries = []
        if (data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKTRANLIST.STMTTRN.length === 0) {
            window.alert('O arquivo está vazio')
            return
        }
        let startDate = data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKTRANLIST.STMTTRN[0].DTPOSTED
        let endDate = new Date(
            data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKTRANLIST.STMTTRN[data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKTRANLIST.STMTTRN.length - 1].DTPOSTED,
        )

        show = true

        endDate.setDate(endDate.getDate() + 1)

        let persisteds: Entry[] = await window.api.financials.entries.getAll({
            from: startDate,
            to: endDate,
        })
        for (const item of data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKTRANLIST.STMTTRN) {
            if (dayjs(item.DTPOSTED).isToday()) continue // não importa o dia atual, pois o OFX é parcial e pode gerar problemas com o ID único
            const persisted: Entry = persisteds.find((el) => el.externalId === item._id)
            let description = item.MEMO.toString().charAt(0).toUpperCase() + item.MEMO.toString().slice(1).toLowerCase() || ''
            description = description.replace(/\s+/g, ' ')
            try {
                let entry: CreateEntryDto = {
                    accountId: account.accountId,
                    description: persisted?.description || description,
                    categoryId: persisted?.categoryId,
                    paymentAt: item.DTPOSTED,
                    personId: persisted?.personId,
                    type: item.TRNAMT > 0 ? EntryTypeEnum.credit : EntryTypeEnum.debit,
                    value: item.TRNAMT > 0 ? item.TRNAMT : item.TRNAMT * -1,
                    source: 'import',
                    reference: item.DTPOSTED,
                    externalId: item._id,
                    classificationId: persisted?.classificationId,
                    accrualMonth: persisted?.accrualMonth,
                    accrualYear: persisted?.accrualYear,
                }
                entries.push(entry)
            } catch (error) {
                console.error('Import failed', error)
                console.error('Import item', item)
                throw error
            }
        }
        entries = await evaluateClassification(entries)
        console.log('ofx-entries', entries)
        await updateAccountSource(account.accountId, 'ofx')
        step = 2
        show = false
    }

    // OPEN FINANCE ====================================================================================
    let refreshOpenFinance
    async function loadOpenFinance() {
        if (!account.pluggyLastSync || dayjs(new Date()).diff(account.pluggyLastSync, 'hour') > 1) {
            await refreshOpenFinance()
        } else {
            await getOpenFinanceTransactions()
        }
    }

    async function onLoadOpenFinance() {
        console.log('step is now ' + step.toString())
        await getOpenFinanceTransactions()
    }

    async function getOpenFinanceTransactions() {
        show = true
        console.log('getOpenFinanceTransactions called')
        let transactions: PluggyTransactionDto
        try {
            transactions = await window.api.financials.accounts.integrationGetTransactions(account.accountId)
        } catch (error) {
            show = false
            // o erro saía daqui em silêncio: a tela só parava de carregar, sem dizer nada. é por aqui que
            // chega o motivo real (banco esperando confirmação, acesso recusado, sincronização em curso)
            window.error(error)
            return
        }
        console.log('transactions', transactions)
        if (!transactions) {
            show = false
            window.error('Ocorreu um erro ao tentar recuperar seu extrato.\nPor favor, tente novamente em alguns minutos.')
            return
        }
        if (!transactions.results || transactions.results.length === 0) {
            show = false
            window.alert('Nenhum dado foi recuperado de seu extrato.')
            return
        }

        entries = []
        let startDate = new Date(transactions.results[transactions.results.length - 1].date)
        let diffDays = dayjs(new Date()).diff(startDate, 'day')
        if (diffDays > 93) {
            startDate = new Date()
            startDate.setHours(0, 0, 0, 0)
            startDate.setDate(startDate.getDate() - 93)
        }
        let endDate = new Date(transactions.results[0].date)
        endDate.setDate(endDate.getDate() + 1)
        let persisteds: Entry[] = await window.api.financials.entries.getAll({
            from: startDate,
            to: endDate,
        })

        for (const item of transactions.results) {
            diffDays = dayjs(new Date()).diff(new Date(item.date), 'day')
            if (diffDays > 93) continue
            if (dayjs(item.date).isToday()) continue // não importa o dia atual, pois o OFX é parcial e pode gerar problemas com o ID único
            const persisted: Entry = persisteds.find((el) => el.externalId === item.id)
            const description = item.description.replace(/\s+/g, ' ')
            try {
                let entry: CreateEntryDto = {
                    accountId: account.accountId,
                    description: persisted?.description || description,
                    categoryId: persisted?.categoryId,
                    paymentAt: item.date,
                    personId: persisted?.personId,
                    type: item.amount >= 0 ? EntryTypeEnum.credit : EntryTypeEnum.debit,
                    value: item.amount > 0 ? item.amount : item.amount * -1,
                    source: 'open',
                    reference: item.date,
                    externalId: item.id,
                    classificationId: persisted?.classificationId,
                    accrualMonth: persisted?.accrualMonth,
                    accrualYear: persisted?.accrualYear,
                }
                entries.push(entry)
            } catch (error) {
                console.error('Import failed', error)
                console.error('Import item', item)
                throw error
            }
        }
        entries.reverse()
        entries = await evaluateClassification(entries)
        console.log('open-entries', entries)
        const result = await updateAccountSource(account.accountId, 'open')
        if (result) account = result
        step = 2
        show = false
    }

    function pluginLoaded() {
        const scripts = document.getElementsByTagName('script')
        for (var i = 0; i < scripts.length; i++) {
            console.log('cripts', scripts[i].src)
            if (scripts[i].src?.includes('cdn.pluggy')) return true
        }
        return false
    }
    // =================================================================================================
</script>

<svelte:head>
    {#if !pluginLoaded()}
        <script src="https://cdn.pluggy.ai/pluggy-connect/v2.6.1/pluggy-connect.js"></script>
    {/if}
</svelte:head>

{#if showClassification}
    <Popover title="Classificação automática" width="1200px" height="85vh" on:close={closeClassification}>
        <Financial_4AccountsClassification
            form={!!classificationSugestion}
            {classificationId}
            sugestion={classificationSugestion}
            on:close={closeClassification}
        ></Financial_4AccountsClassification>
    </Popover>
{/if}

{#if step === 1}
    <div class="root">
        <div class="grid-act">
            <button class="btn btn-secondary" on:click={goClassification}><Fa icon={faRobot} /> Classificação automática</button>

            <div class="spacer" />
            {#if window.store.token.permissions.financial.entries.write}
                <button class="btn btn-primary" on:click={() => startImport()}>
                    <Fa icon={faArrowRight} /> Avançar
                </button>
            {/if}
        </div>
        <div class="grid-form">
            <div class="row">
                <div class="col-6">
                    <AccountSelect label="Conta a ser importada" bind:account />
                </div>
            </div>
            {#if account}
                <div class="row mt-3">
                    <div class="col">
                        <div class="form-check form-check-inline">
                            <input type="radio" name="source" bind:group={account.source} value="open" id="open" class="form-check-input" />
                            <label for="open">Open Finance (beta)</label>
                        </div>
                        <div class="text-muted mb-1">
                            Importe automaticamente seu extrato bancário para o Zandu através do Open Finance do Banco Central.<br />
                            Essa é a maneira mais segura e rápida para realizar sua gestão financeira.
                        </div>
                    </div>
                </div>
                {#if account.source === 'open'}
                    <div class="row">
                        <div class="col">
                            <PluggyConnector
                                bind:refresh={refreshOpenFinance}
                                accountId={account.accountId}
                                showTrash={true}
                                showRefresh={false}
                                pluggyId={account.pluggyId}
                                on:connected={onLoadOpenFinance}
                            ></PluggyConnector>
                        </div>
                    </div>
                {/if}
                <div class="row mt-3">
                    <div class="col">
                        <div class="form-check form-check-inline">
                            <input type="radio" name="source" bind:group={account.source} value="ofx" id="ofx" class="form-check-input" />
                            <label for="ofx">Arquivo OFX</label>
                        </div>
                        <div class="text-muted mb-1">
                            Os bancos fornecem seu extrato para serem baixados no formato OFX, que podem ser importados pelo Zandu.<br />
                            Para baixar seu extrato bancário no formato OFX para o seu computador:<br />
                            <ul>
                                <li>Acesse seu Internet Banking</li>
                                <li>Vá até a área de extratos</li>
                                <li>Salve / exporte seu extrato no formato OFX (OFX Money) para seu computador</li>
                                <li>Após baixar do Internet Banking você poderá importá-lo aqui.</li>
                            </ul>
                        </div>
                    </div>
                </div>
                {#if account.source === 'ofx'}
                    <div class="row">
                        <div class="col-4">
                            <input class="form-control" type="file" accept=".ofx" id="formFile" on:change={fileChanged} bind:this={file} />
                        </div>
                    </div>
                {/if}
            {/if}
        </div>
    </div>
{/if}

{#if step === 2}
    <div class="root mb-4">
        <div class="grid-act">
            <div class="btn-group">
                <input type="radio" class="btn-check" value={999} name="btndays" bind:group={filterDays} id="btndays999" autocomplete="off" />
                <label class="btn btn-primary" for="btndays999">Tudo</label>

                <input type="radio" class="btn-check" value={30} name="btndays" bind:group={filterDays} id="btndays30" autocomplete="off" />
                <label class="btn btn-primary" for="btndays30">Últimos 30 dias</label>

                <input type="radio" class="btn-check" value={15} name="btndays" bind:group={filterDays} id="btndays15" autocomplete="off" />
                <label class="btn btn-primary" for="btndays15">Últimos 15 dias</label>

                <input type="radio" class="btn-check" value={7} name="btndays" bind:group={filterDays} id="btndays7" autocomplete="off" />
                <label class="btn btn-primary" for="btndays7">Últimos 7 dias</label>

                <input type="radio" class="btn-check" value={3} name="btndays" bind:group={filterDays} id="btndays3" autocomplete="off" />
                <label class="btn btn-primary" for="btndays3">Últimos 3 dias</label>
            </div>
            <div class="spacer" />
            <!-- {#if window.store.token.permissions.financial.entries.write}
                <button class="btn btn-primary" on:click={() => saveEntity()}>
                    <Fa icon={faSave} /> Salvar
                </button>
            {/if} -->
            <button class="btn btn-primary" on:click={() => close()}>
                <Fa icon={faCheck} /> Concluir
            </button>
        </div>

        <!-- <div class="grid-form px-2">
            <div class="row align-middle">
                <div class="col-1">Data</div>
                <div class="col nowrap">Descrição</div>
                <div class="col-1 text-end">Valor</div>
                <div class="col">Categoria</div>
                <div class="col-2" />
            </div>
            {#each entries as entry}
                <Financial_4AccountsImportItem
                    {entry}
                    categories={entry.type === EntryTypeEnum.credit ? categoriesCredit : categoriesDebit}
                    on:change={onEntryChanged}
                />
            {/each}
        </div> -->

        <div class="financial-import-items">
            {#each entries as entry, index}
                {@const diffDays = dayjs(new Date()).diff(entry.paymentAt, 'day')}

                {#if diffDays <= filterDays}
                    <Financial_4AccountsImportItemV2
                        {entry}
                        categories={entry.type === EntryTypeEnum.credit ? categoriesCredit : categoriesDebit}
                        on:classification={goClassificationForm}
                        on:change={onEntryChanged}
                        position={index + 1}
                        total={entries.length}
                        bind:accrualMonth={entry.accrualMonth}
                        bind:accrualYear={entry.accrualYear}
                    />
                {/if}
            {/each}
        </div>
        <div class="text-center">
            <button class="btn btn-primary mt-3" on:click={() => close()}>
                <Fa icon={faCheck} /> Concluir
            </button>
        </div>
    </div>
{/if}
<LoadSpinner delay={0} {show}></LoadSpinner>

<style>
    .root {
        display: flex;
        flex-direction: column;
    }

    .row {
        padding: 0px 16px;
    }

    .financial-import-items {
        padding: 16px;
    }
</style>
